import { useState, useEffect } from 'react';
import { formatDate, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import Panel from '../Panel';
import ApplicationApprovalModal from './ApplicationApprovalModal';
import { approveCampaignApplication } from '~/services/campaign';
import { ApplicationCompletionStatus } from '~/types/application-completion';
import { toast } from 'react-hot-toast';
import { getCampaignStatusBadgeClass } from '~/utils/campaignBadgeStyles';

interface Props {
    campaign?: any;
    campaignRequest?: any;
    application?: any;
    onApplicationApproved?: (fundId?: string) => void;
    completionStatus?: ApplicationCompletionStatus | null;
}

const TitlePanel: React.FC<Props> = ({ 
    campaign, 
    campaignRequest, 
    application,
    onApplicationApproved,
    completionStatus,
}) => {
    const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const navigate = useNavigate();

    // Normalize campaign status to support both string and object responses
    const statusName: string | undefined = (() => {
        const cs = campaign?.campaign_status;
        if (!cs) return undefined;
        return typeof cs === 'string' ? cs : cs?.name;
    })();

    // Stable status code — gate behavior on this, never on the display label,
    // which staff can rename (HHLTB-1945).
    const statusCode: string | undefined = (() => {
        const cs = campaign?.campaign_status;
        if (cs && typeof cs !== 'string' && cs?.code) return cs.code;
        return campaign?.campaign_status_code;
    })();

    // Check if all sections are complete
    const isApplicationComplete = completionStatus?.overall_complete || false;

    // Statuses that indicate the campaign is already approved/published
    const publishedStatusCodes = ['funded', 'disbursable', 'closed', 'archived'];
    const isAlreadyPublished = statusCode ? publishedStatusCodes.includes(statusCode) : false;

    // Check if application can be approved (all sections complete and not already published)
    const canApprove = isApplicationComplete && !isAlreadyPublished;

    // Debug logging
    useEffect(() => {
        if (campaign && completionStatus) {
            console.log('Campaign Approval Debug:', {
                statusName,
                isApplicationComplete,
                isAlreadyPublished,
                canApprove,
                campaignId: campaign?.id,
            });
        }
    }, [campaign, completionStatus, statusName, isApplicationComplete, isAlreadyPublished, canApprove]);

    // Get patient name for modal
    const getPatientName = () => {
        return campaign?.full_name || 'Unknown Patient';
    };

    const handleApproval = async (comments: string) => {
        if (!application?.id) {
            toast.error('Application ID not found');
            return;
        }

        try {
            setIsApproving(true);
            const response = await approveCampaignApplication(application.id, comments);
            
            toast.success('Application approved successfully');
            setIsApprovalModalOpen(false);
            
            // Extract fund_id from response if available
            const fundId = response?.application?.campaign?.nxt_fund_id;
            
            // Call the callback to refresh data and optionally show fund details
            if (onApplicationApproved) {
                onApplicationApproved(fundId);
            }
        } catch (error: any) {
            console.error('Error approving application:', error);
            toast.error(error.message || 'Failed to approve application');
        } finally {
            setIsApproving(false);
        }
    };

    return (
        <div className='mb-7.5'>
            <Panel>
                <div className='xl:flex xl:justify-between'>
                    <div className='mb-6 xl:mb-0'>
                        <h1 className='mb-4 text-[38px]/[24px] font-medium'>
                            {campaign?.full_name}
                        </h1>
                        <div className='flex flex-col gap-2'>
                            <div className='flex items-center'>
                                <div
                                    className={`inline-flex rounded-full px-2 py-1 text-sm ${getCampaignStatusBadgeClass(statusName)}`}
                                >
                                    {statusName ?? 'Unknown'}
                                </div>
                            </div>
                            <div className='flex items-center gap-[16px]'>
                                <div className='text-[16px]/[24px] text-[#949599]'>
                                    Last Updated:{' '}
                                    {campaign?.update_date &&
                                        formatDate(
                                            parseISO(campaign?.update_date),
                                            'MM/dd/yy'
                                        )}
                                </div>
                                {application?.ag_signature && (
                                    <div className='text-[16px]/[24px] text-[#949599]'>
                                        Signed by: {application.ag_signature}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className='xl:flex xl:flex-col xl:items-end'>
                        <div className='flex items-center gap-4'>
                            {campaign?.id && (
                                <button
                                    onClick={() => navigate(`/campaigns/${campaign.id}`)}
                                    className='rounded-full border-[2px] border-primary bg-primary px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 hover:bg-opacity-90'
                                >
                                    Console
                                </button>
                            )}
                            {campaign?.id && (
                                <button
                                    onClick={() =>
                                        window.open(
                                            `${import.meta.env.VITE_CLIENT_URL}/dashboard?campaignId=${campaign.id}`,
                                            '_blank'
                                        )
                                    }
                                    className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                                >
                                    Dashboard
                                </button>
                            )}
                            {campaignRequest?.id && (
                                <button
                                    onClick={() => navigate(`/campaign-requests/${campaignRequest.id}`)}
                                    className='rounded-full border-[2px] border-stroke bg-white px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-body transition-colors duration-500 hover:border-bodydark hover:bg-gray-2'
                                >
                                    Campaign Request
                                </button>
                            )}
                            {/* Only show Approve Application button if status is not Funded or Disbursable */}
                            {statusCode !== 'funded' && statusCode !== 'disbursable' && (
                                <button
                                    onClick={() => setIsApprovalModalOpen(true)}
                                    className={`rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 ${
                                        canApprove && completionStatus
                                            ? 'border-warning bg-warning text-black hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                                            : 'cursor-not-allowed border-[#F4F4F5] bg-[#F4F4F5] text-[#949599]'
                                    }`}
                                    disabled={!canApprove || !completionStatus}
                                    title={
                                        !completionStatus
                                            ? 'Loading completion status...'
                                            : !isApplicationComplete
                                            ? 'All application sections must be complete before approval'
                                            : isAlreadyPublished
                                            ? 'This campaign has already been approved and published'
                                            : 'Approve this application'
                                    }
                                >
                                    Approve Application
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </Panel>

            <ApplicationApprovalModal
                isOpen={isApprovalModalOpen}
                onClose={() => setIsApprovalModalOpen(false)}
                onConfirm={handleApproval}
                isLoading={isApproving}
                patientName={getPatientName()}
            />
        </div>
    );
};

export default TitlePanel;
